/**
 * Pure inline-crop geometry for the native Rust overlay (pan / zoom / rotate a photo within its frame).
 * Ported verbatim from the Konva crop editor (crop-math/crop-state/inline-crop-math) — no Konva/DOM deps,
 * so it is unit-testable and reusable by RustOverlayController. All coordinates are in book/origin px.
 */

export const MAX_ROTATION = 90;
export const ROTATION_MAGNET_THRESHOLD = 2;
/**
 * Legacy vignette/person-cell ceiling (DTO `@Max(2.5)`): a new zoom never climbs to it, but a stored scale
 * above the crop ceiling re-enters through `scaleFloor`.
 */
export const MAX_SCALE_MULTIPLIER = 2.5;
/** Inline crop ceiling: 2× the cover minimum at 0°, as in the legacy editor. */
export const CROP_MAX_SCALE_MULTIPLIER = 2;
export const BINARY_SEARCH_ITERATIONS_OFFSET = 20;
export const BINARY_SEARCH_ITERATIONS_PROJECTION = 12;
export const DIAGONAL_PROGRESS_THRESHOLD = 0.1;
export const GEOMETRY_EPSILON = 1e-6;

/** Wheel/pinch sensitivity: a trackpad two-finger scroll or ~8 mouse notches walk the zoom range. */
export const WHEEL_ZOOM_SENSITIVITY = 0.15;
export const WHEEL_ZOOM_MAX_DELTA = 12;
/** Trackpad pinch (wheel events with ctrlKey) rides the same tuning. */
export const PINCH_ZOOM_BOOST = 1;
export const WHEEL_ROTATION_SENSITIVITY = 0.025;
export const WHEEL_ROTATION_MAX_DELTA = 2;

/** Working crop state — `scale` multiplies natural dims; `offsetX/Y` is the image-centre displacement BEFORE rotation. */
export interface CropState {
  readonly cropWidth: number;
  readonly cropHeight: number;
  readonly imageNaturalWidth: number;
  readonly imageNaturalHeight: number;
  scale: number;
  rotation: number;
  offsetX: number;
  offsetY: number;
  /** Entry scale of an already-saved crop above the ceiling — it lifts the session maximum so the photo is never shrunk. */
  scaleFloor?: number;
}

/** Rendered photo placement within the frame (book px, container-relative) — the `imageRect`/`naturalAligns.image` shape. */
export interface CropImageRect {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

export interface SizeLike {
  readonly width: number;
  readonly height: number;
}

interface Point {
  x: number;
  y: number;
}

function rotatePoint(p: Point, angleDeg: number): Point {
  const rad = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return { x: p.x * cos - p.y * sin, y: p.x * sin + p.y * cos };
}

function getRotatedImageCornersInCropSpace(
  imgWidth: number,
  imgHeight: number,
  offsetX: number,
  offsetY: number,
  rotation: number,
): [Point, Point, Point, Point] {
  const hw = imgWidth / 2;
  const hh = imgHeight / 2;
  const before: [Point, Point, Point, Point] = [
    { x: offsetX - hw, y: offsetY - hh },
    { x: offsetX + hw, y: offsetY - hh },
    { x: offsetX + hw, y: offsetY + hh },
    { x: offsetX - hw, y: offsetY + hh },
  ];
  return before.map(c => rotatePoint(c, rotation)) as [Point, Point, Point, Point];
}

function isPointInsideTriangle(p: Point, a: Point, b: Point, c: Point): boolean {
  const v0x = c.x - a.x;
  const v0y = c.y - a.y;
  const v1x = b.x - a.x;
  const v1y = b.y - a.y;
  const v2x = p.x - a.x;
  const v2y = p.y - a.y;
  const dot00 = v0x * v0x + v0y * v0y;
  const dot01 = v0x * v1x + v0y * v1y;
  const dot02 = v0x * v2x + v0y * v2y;
  const dot11 = v1x * v1x + v1y * v1y;
  const dot12 = v1x * v2x + v1y * v2y;
  const invDenom = 1 / (dot00 * dot11 - dot01 * dot01);
  const u = (dot11 * dot02 - dot01 * dot12) * invDenom;
  const v = (dot00 * dot12 - dot01 * dot02) * invDenom;
  return u >= -GEOMETRY_EPSILON && v >= -GEOMETRY_EPSILON && u + v <= 1 + GEOMETRY_EPSILON;
}

function isPointInsideQuad(point: Point, corners: [Point, Point, Point, Point]): boolean {
  return (
    isPointInsideTriangle(point, corners[0], corners[1], corners[2]) ||
    isPointInsideTriangle(point, corners[0], corners[2], corners[3])
  );
}

export function calculateMinScale(state: CropState): number {
  const { cropWidth, cropHeight, imageNaturalWidth, imageNaturalHeight, rotation } = state;
  const rad = Math.abs((rotation * Math.PI) / 180);
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const minImageWidth = cropWidth * cos + cropHeight * sin;
  const minImageHeight = cropWidth * sin + cropHeight * cos;
  return Math.max(minImageWidth / imageNaturalWidth, minImageHeight / imageNaturalHeight);
}

export function calculateMaxScale(state: CropState): number {
  const ceiling = calculateMinScale({ ...state, rotation: 0 }) * CROP_MAX_SCALE_MULTIPLIER;
  return Math.max(ceiling, state.scaleFloor ?? 0);
}

export function getScaledImageSize(state: CropState): { width: number; height: number } {
  return { width: state.imageNaturalWidth * state.scale, height: state.imageNaturalHeight * state.scale };
}

function checkCoverage(
  imgW: number,
  imgH: number,
  cropWidth: number,
  cropHeight: number,
  offsetX: number,
  offsetY: number,
  rotation: number,
): boolean {
  const cropCorners: [Point, Point, Point, Point] = [
    { x: -cropWidth / 2, y: -cropHeight / 2 },
    { x: cropWidth / 2, y: -cropHeight / 2 },
    { x: cropWidth / 2, y: cropHeight / 2 },
    { x: -cropWidth / 2, y: cropHeight / 2 },
  ];
  const imageCorners = getRotatedImageCornersInCropSpace(imgW, imgH, offsetX, offsetY, rotation);
  return cropCorners.every(corner => isPointInsideQuad(corner, imageCorners));
}

export function isCropFullyCovered(state: CropState): boolean {
  const { width: imgW, height: imgH } = getScaledImageSize(state);
  return checkCoverage(imgW, imgH, state.cropWidth, state.cropHeight, state.offsetX, state.offsetY, state.rotation);
}

function findMaxOffset(
  imgW: number,
  imgH: number,
  cropWidth: number,
  cropHeight: number,
  rotation: number,
  direction: 'x' | 'y',
  sign: 1 | -1,
): number {
  let low = 0;
  let high = direction === 'x' ? imgW : imgH;
  for (let i = 0; i < BINARY_SEARCH_ITERATIONS_OFFSET; i++) {
    const mid = (low + high) / 2;
    const testX = direction === 'x' ? mid * sign : 0;
    const testY = direction === 'y' ? mid * sign : 0;
    if (checkCoverage(imgW, imgH, cropWidth, cropHeight, testX, testY, rotation)) low = mid;
    else high = mid;
  }

  return low;
}

export function constrainOffset(state: CropState): { offsetX: number; offsetY: number } {
  const { cropWidth, cropHeight, rotation, offsetX, offsetY } = state;
  const { width: imgW, height: imgH } = getScaledImageSize(state);
  if (checkCoverage(imgW, imgH, cropWidth, cropHeight, offsetX, offsetY, rotation)) return { offsetX, offsetY };

  const maxPosX = findMaxOffset(imgW, imgH, cropWidth, cropHeight, rotation, 'x', 1);
  const maxNegX = findMaxOffset(imgW, imgH, cropWidth, cropHeight, rotation, 'x', -1);
  const maxPosY = findMaxOffset(imgW, imgH, cropWidth, cropHeight, rotation, 'y', 1);
  const maxNegY = findMaxOffset(imgW, imgH, cropWidth, cropHeight, rotation, 'y', -1);

  let newOffsetX = offsetX;
  let newOffsetY = offsetY;
  if (newOffsetX > maxPosX) newOffsetX = maxPosX;
  if (newOffsetX < -maxNegX) newOffsetX = -maxNegX;
  if (newOffsetY > maxPosY) newOffsetY = maxPosY;
  if (newOffsetY < -maxNegY) newOffsetY = -maxNegY;

  if (!checkCoverage(imgW, imgH, cropWidth, cropHeight, newOffsetX, newOffsetY, rotation)) {
    newOffsetX = 0;
    newOffsetY = 0;
  }

  return { offsetX: newOffsetX, offsetY: newOffsetY };
}

export function constrainScale(state: CropState): number {
  return Math.max(calculateMinScale(state), Math.min(state.scale, calculateMaxScale(state)));
}

function findMaxAlongAxis(
  imgW: number,
  imgH: number,
  cropWidth: number,
  cropHeight: number,
  rotation: number,
  baseX: number,
  baseY: number,
  axis: 'x' | 'y',
  delta: number,
): number {
  if (delta === 0) return axis === 'x' ? baseX : baseY;
  let low = 0;
  let high = 1;
  for (let i = 0; i < BINARY_SEARCH_ITERATIONS_PROJECTION; i++) {
    const mid = (low + high) / 2;
    const testX = axis === 'x' ? baseX + delta * mid : baseX;
    const testY = axis === 'y' ? baseY + delta * mid : baseY;
    if (checkCoverage(imgW, imgH, cropWidth, cropHeight, testX, testY, rotation)) low = mid;
    else high = mid;
  }

  return (axis === 'x' ? baseX : baseY) + delta * low;
}

export function projectOffsetToValidRegion(
  state: CropState,
  currentOffsetX: number,
  currentOffsetY: number,
  desiredOffsetX: number,
  desiredOffsetY: number,
): { offsetX: number; offsetY: number } {
  const { cropWidth, cropHeight, rotation } = state;
  const { width: imgW, height: imgH } = getScaledImageSize(state);
  const deltaX = desiredOffsetX - currentOffsetX;
  const deltaY = desiredOffsetY - currentOffsetY;

  if (checkCoverage(imgW, imgH, cropWidth, cropHeight, desiredOffsetX, desiredOffsetY, rotation)) {
    return { offsetX: desiredOffsetX, offsetY: desiredOffsetY };
  }

  if (!checkCoverage(imgW, imgH, cropWidth, cropHeight, currentOffsetX, currentOffsetY, rotation)) {
    return { offsetX: 0, offsetY: 0 };
  }

  let low = 0;
  let high = 1;
  for (let i = 0; i < BINARY_SEARCH_ITERATIONS_PROJECTION; i++) {
    const mid = (low + high) / 2;
    const testX = currentOffsetX + deltaX * mid;
    const testY = currentOffsetY + deltaY * mid;
    if (checkCoverage(imgW, imgH, cropWidth, cropHeight, testX, testY, rotation)) low = mid;
    else high = mid;
  }

  const diagonalX = currentOffsetX + deltaX * low;
  const diagonalY = currentOffsetY + deltaY * low;
  if (low > DIAGONAL_PROGRESS_THRESHOLD) return { offsetX: diagonalX, offsetY: diagonalY };

  const xFirst = findMaxAlongAxis(imgW, imgH, cropWidth, cropHeight, rotation, currentOffsetX, currentOffsetY, 'x', deltaX);
  const yAfterX = findMaxAlongAxis(imgW, imgH, cropWidth, cropHeight, rotation, xFirst, currentOffsetY, 'y', deltaY);
  const yFirst = findMaxAlongAxis(imgW, imgH, cropWidth, cropHeight, rotation, currentOffsetX, currentOffsetY, 'y', deltaY);
  const xAfterY = findMaxAlongAxis(imgW, imgH, cropWidth, cropHeight, rotation, currentOffsetX, yFirst, 'x', deltaX);

  const distA = Math.hypot(xFirst - desiredOffsetX, yAfterX - desiredOffsetY);
  const distB = Math.hypot(xAfterY - desiredOffsetX, yFirst - desiredOffsetY);
  return distA <= distB ? { offsetX: xFirst, offsetY: yAfterX } : { offsetX: xAfterY, offsetY: yFirst };
}

export function scaleToPercent(state: CropState): number {
  const minScale = calculateMinScale(state);
  const range = calculateMaxScale(state) - minScale;
  if (range <= 0) return 0;
  return ((state.scale - minScale) / range) * 100;
}

export function percentToScale(state: CropState, percent: number): number {
  const minScale = calculateMinScale(state);
  const range = calculateMaxScale(state) - minScale;
  return minScale + (range * percent) / 100;
}

export function offsetToBackendModel(
  offsetX: number,
  offsetY: number,
  rotationDeg: number,
): { offsetX: number; offsetY: number } {
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return { offsetX: offsetX * cos - offsetY * sin, offsetY: offsetX * sin + offsetY * cos };
}

export function offsetFromBackendModel(
  rotatedOffsetX: number,
  rotatedOffsetY: number,
  rotationDeg: number,
): { offsetX: number; offsetY: number } {
  const rad = (-rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return { offsetX: rotatedOffsetX * cos - rotatedOffsetY * sin, offsetY: rotatedOffsetX * sin + rotatedOffsetY * cos };
}

function createInitialCropState(cropWidth: number, cropHeight: number, natW: number, natH: number): CropState {
  return {
    cropWidth,
    cropHeight,
    imageNaturalWidth: natW,
    imageNaturalHeight: natH,
    scale: 1,
    rotation: 0,
    offsetX: 0,
    offsetY: 0,
  };
}

export function cloneCropState(state: CropState): CropState {
  return { ...state };
}

/** Build a valid CropState from a container size + existing imageRect (or null = fresh cover-fit) + natural dims. */
export function createCropStateFromRects(
  container: SizeLike,
  imageRect: CropImageRect | null,
  imageNatural: SizeLike,
): CropState | null {
  if (!imageNatural.width || !imageNatural.height) return null;
  const state = createInitialCropState(container.width, container.height, imageNatural.width, imageNatural.height);

  if (imageRect) {
    const rotation = imageRect.rotation ?? 0;
    state.scale = imageRect.width / imageNatural.width;
    state.scaleFloor = state.scale; // a book saved above today's ceiling opens as it was — it may shrink, never grow
    state.rotation = rotation;
    const containerCenterX = container.width / 2;
    const containerCenterY = container.height / 2;
    const imgCenterX = imageRect.x + imageRect.width / 2;
    const imgCenterY = imageRect.y + imageRect.height / 2;
    const { offsetX, offsetY } = offsetFromBackendModel(imgCenterX - containerCenterX, imgCenterY - containerCenterY, rotation);
    state.offsetX = offsetX;
    state.offsetY = offsetY;
  }

  state.scale = constrainScale(state);
  const constrained = constrainOffset(state);
  state.offsetX = constrained.offsetX;
  state.offsetY = constrained.offsetY;
  return state;
}

/** Convert a CropState back to a container-relative imageRect for the renderer / model commit. */
export function cropStateToImageRect(state: CropState): CropImageRect {
  const imgW = state.imageNaturalWidth * state.scale;
  const imgH = state.imageNaturalHeight * state.scale;
  const { offsetX: rotatedOffsetX, offsetY: rotatedOffsetY } = offsetToBackendModel(state.offsetX, state.offsetY, state.rotation);
  return {
    x: state.cropWidth / 2 + rotatedOffsetX - imgW / 2,
    y: state.cropHeight / 2 + rotatedOffsetY - imgH / 2,
    width: imgW,
    height: imgH,
    rotation: state.rotation,
  };
}

// ==================== Gesture transforms (return a new CropState) ====================

/** Pan: book-space pointer delta → new offset (un-rotates the delta, then slides along the cover constraint). */
export function panState(
  state: CropState,
  startOffsetX: number,
  startOffsetY: number,
  bookDeltaX: number,
  bookDeltaY: number,
): CropState {
  const rad = (-state.rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const deltaX = bookDeltaX * cos - bookDeltaY * sin;
  const deltaY = bookDeltaX * sin + bookDeltaY * cos;
  const { offsetX, offsetY } = projectOffsetToValidRegion(
    state,
    startOffsetX,
    startOffsetY,
    startOffsetX + deltaX,
    startOffsetY + deltaY,
  );
  return { ...state, offsetX, offsetY };
}

/** Scale by a distance ratio (edge handle / pinch); re-constrains scale then offset so the frame stays covered. */
export function scaleStateByRatio(state: CropState, startScale: number, ratio: number): CropState {
  const next = { ...state, scale: startScale * ratio };
  next.scale = constrainScale(next);
  const { offsetX, offsetY } = constrainOffset(next);
  return { ...next, offsetX, offsetY };
}

/** Zoom by a scale-range percent delta (wheel/pinch). */
export function zoomStateByPercentDelta(state: CropState, percentDelta: number): CropState {
  const current = scaleToPercent(state);
  const next = { ...state, scale: percentToScale(state, Math.max(0, Math.min(100, current + percentDelta))) };
  // A rotated crop can have its cover minimum above the 0° ceiling: the percent range collapses and the
  // interpolation lands below coverage, so the scale is clamped like every other zoom path.
  next.scale = constrainScale(next);
  const { offsetX, offsetY } = constrainOffset(next);
  return { ...next, offsetX, offsetY };
}

/** Clamp a candidate rotation to ±MAX_ROTATION, then magnet it to 0° whenever it lands inside the threshold. */
export function clampAndMagnetRotation(candidate: number): number {
  const next = Math.max(-MAX_ROTATION, Math.min(MAX_ROTATION, candidate));
  return Math.abs(next) < ROTATION_MAGNET_THRESHOLD ? 0 : next;
}

/** Rotate with Lightroom-style auto-scale: preserve the user's extra zoom above the cover minimum across the new angle.
 *  The candidate is the raw gesture angle — clamp and magnet run here, so every application is snapped. */
export function applyRotationWithAutoScale(state: CropState, newRotation: number): CropState {
  const prevMinScale = calculateMinScale(state);
  const zoomRatio = prevMinScale > 0 ? state.scale / prevMinScale : 1;
  const rotated = { ...state, rotation: clampAndMagnetRotation(newRotation) };
  const newMinScale = calculateMinScale(rotated);
  const newMaxScale = calculateMaxScale(rotated);
  rotated.scale = Math.max(newMinScale, Math.min(newMaxScale, newMinScale * zoomRatio));
  const { offsetX, offsetY } = constrainOffset(rotated);
  return { ...rotated, offsetX, offsetY };
}
